import { useLocation } from 'react-router-dom';

import {
  useGiftGuideHubGate,
  useGiftGuideHubOverride,
} from '../hub/giftGuideHubFlag';

/**
 * The ONE resolver for Fresh Find Fridays (THE-746 plan rev 4, Phase 4;
 * THE-769 / PR 4.4; ridden onto the hub switch by THE-879).
 *
 *   freshFindFridaysGate = ?freshFindFridays=1 -> 'enabled'  (preview cohort,
 *                            this feature's OWN alias)
 *                          : giftGuideHubGate                (THE-879: this
 *                              feature has no release switch of its own —
 *                              see below)
 *
 * NO THIRD READ (THE-879, Kate). Fresh Find Fridays and the Gift Guide Hub
 * ship together — weekly refreshes are what both depend on — so the
 * FRONTEND gate here is not its own general-release switch any more, it is
 * the hub's `useGiftGuideHubGate()` reused wholesale: `?giftGuideHub=1` OR
 * `featureFlags/giftGuideHub.enabled`. `?freshFindFridays=1` survives as a
 * second, narrower preview-cohort door (a reviewer who only wants THIS card,
 * not the whole hub) but no longer backs a Firestore doc of its own —
 * `featureFlags/freshFindFridays` is now read ONLY by the BACKEND weekly job
 * (`theaWebFreshFindFridays`, thea-serverless, THE-766), which spends money
 * running the job and is deliberately left on its own switch rather than
 * folded into this one. The hub launch checklist (THE-816) is the thing that
 * keeps the two in sync at launch time, not shared frontend code.
 *
 * THREE STATES, NOT TWO (the THE-664 defect), INHERITED FROM THE HUB. `pending`
 * is a first-class value: the release door reaches its answer through
 * `ensureAuth()` + `getDoc()`, so every visitor's FIRST render has no answer
 * yet. A consumer that only distinguishes "on" from "not on" renders the
 * signed-in People header twice — once without the card and once with it —
 * which is a visible jump on the page Kate looks at most.
 * `FreshFindFridaysCard`'s host renders nothing on `pending` and nothing on
 * `disabled`; only `enabled` mounts the card.
 *
 * TWO OVERRIDE DOORS, ONE RELEASE SWITCH:
 *
 *  - `?giftGuideHub=1` is the hub's cohort door, and since THE-879 it also
 *    unlocks this card — the two ship together, so a hub preview link should
 *    show everything the hub launch shows.
 *
 *  - `?freshFindFridays=1` is this feature's OWN alias for the same door, kept
 *    working so any existing preview link (THE-795 screenshots, etc.) still
 *    resolves `enabled` without the caller needing to know the hub's param.
 *
 *  - `featureFlags/giftGuideHub.enabled` is the ONE general-release switch
 *    both features answer to now. See `hub/giftGuideHubFlag.ts` for the
 *    fail-closed/rules-block reasoning — it applies here unchanged because
 *    this file no longer does its own Firestore read at all.
 */

/** Query param carrying this feature's OWN preview-cohort alias. */
export const FRESH_FIND_FRIDAYS_PARAM = 'freshFindFridays';

/** The only value that activates the override. Any other value is ignored. */
export const FRESH_FIND_FRIDAYS_PARAM_ON = '1';

/**
 * The resolved state of the gate. `pending` means the hub's general-release
 * read has not answered yet and NOTHING has been decided — it is not a
 * synonym for `disabled`. Every consumer branches on all three. There is
 * deliberately no boolean wrapper to reach for: `'disabled'` is a truthy
 * string, so `if (!gate)` would be quietly always-false.
 */
export type FreshFindFridaysGate = 'pending' | 'enabled' | 'disabled';

/**
 * Is the cohort override present in this search string? Pure, so a test can
 * drive it without a Router.
 */
export function hasFreshFindFridaysOverride(search: string): boolean {
  if (!search) return false;
  try {
    return (
      new URLSearchParams(search).get(FRESH_FIND_FRIDAYS_PARAM) ===
      FRESH_FIND_FRIDAYS_PARAM_ON
    );
  } catch {
    // A malformed query string is not a reason to crash the People page.
    return false;
  }
}

/**
 * Is the cohort override on for the CURRENT location? Reactive on the URL, so
 * a client-side nav that drops or adds the param flips it without a remount.
 */
export function useFreshFindFridaysOverride(): boolean {
  const { search } = useLocation();
  return hasFreshFindFridaysOverride(search);
}

export interface UseFreshFindFridaysGateOptions {
  /**
   * Skip the hub's release-switch read entirely. A parameter rather than an
   * `if` because hooks can't be conditional (the same opt-out shape
   * `useBoardGuideRailEnabled(skip)` and `useGiftGuideHubGate(skip)` use).
   */
  skip?: boolean;
}

/**
 * The resolved gate: THIS feature's own cohort alias, OR the hub's gate
 * (cohort override OR hub's general release), as one of three states.
 * Reactive on the URL for the same reason the hub's resolver is.
 *
 * `?freshFindFridays=1` wins and resolves SYNCHRONOUSLY, same as the hub's own
 * `?giftGuideHub=1` — a preview link never sees `pending`. Everything else
 * (including the hub-release read) is delegated to `useGiftGuideHubGate` —
 * THE-879's "don't add a third read": this hook no longer owns a Firestore
 * doc of its own.
 */
export function useFreshFindFridaysGate(
  options: UseFreshFindFridaysGateOptions = {},
): FreshFindFridaysGate {
  const ownOverride = useFreshFindFridaysOverride();
  const hubOverride = useGiftGuideHubOverride();
  const override = ownOverride || hubOverride;
  // When either override already decided `enabled`, the hub's release read
  // can only agree — skip it rather than spend a (currently denied)
  // Firestore round trip. `skip` also forwards the caller's own opt-out.
  const hubGate = useGiftGuideHubGate({
    skip: options.skip === true || override,
  });
  return override ? 'enabled' : hubGate;
}
